import { useEffect, useMemo, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { cleanText } from '@/utils/sanitize';
import { extractVisibleText, isHtmlEffectivelyEmpty } from '@/utils/htmlContent';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save, AlertCircle, Edit as EditIcon, Eye, Trash2, Clock, BarChart3, FileText, Type, Sparkles, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { courseService } from '@/services/course.service';
import { ContentItemFormData, getContentItemValidationSchema } from '@/validations/contentItemValidation';
import VisualContentEditor from '@/components/content-editor/VisualContentEditor';
import MediaUploader from '@/components/content-editor/MediaUploader';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import { ContentItem } from '@/types/course.types';
import { AVAILABLE_THEMES, detectTheme, applyTheme } from '@/utils/themeManager';
import SimpleSelect from '@/components/ui/SimpleSelect';

// Utility function to strip leading icons from learning-objectives list items
const stripLeadingIconsFromContent = (html: string): string => {
  // List of icon characters to remove (comprehensive list)
  const iconChars = '✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍';
  const iconPattern = /^[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*/;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');

  // Find ALL learning-objectives and key-takeaways
  const allComponents = doc.querySelectorAll('.learning-objectives, .key-takeaways');

  allComponents.forEach((component) => {
    const listItems = component.querySelectorAll('li');
    listItems.forEach((li) => {
      // Method 1: Check if first child is an element containing only icons
      const firstChild = li.firstChild;
      if (firstChild && firstChild.nodeType === Node.ELEMENT_NODE) {
        const el = firstChild as HTMLElement;
        const text = el.textContent?.trim() || '';
        // If the first element only contains icon characters (1-3 chars), remove it
        if (text.length <= 3 && [...text].every(c => iconChars.includes(c))) {
          el.remove();
        }
      }

      // Method 2: Handle text node with leading icons
      const currentFirstChild = li.firstChild;
      if (currentFirstChild && currentFirstChild.nodeType === Node.TEXT_NODE) {
        const text = currentFirstChild.textContent || '';
        const cleaned = text.replace(iconPattern, '');
        if (cleaned !== text) {
          currentFirstChild.textContent = cleaned;
        }
      }

      // Method 3: Clean innerHTML directly - remove icon spans and leading icons
      let innerHTML = li.innerHTML;
      // Remove span elements that contain only icons (with any attributes)
      innerHTML = innerHTML.replace(/<span[^>]*>[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*<\/span>[\s]*/gi, '');
      // Remove leading icons from text
      innerHTML = innerHTML.replace(iconPattern, '');

      if (innerHTML !== li.innerHTML) {
        li.innerHTML = innerHTML;
      }
    });

    // Set default data attribute if missing
    if (!component.hasAttribute('data-use-custom-icon')) {
      component.setAttribute('data-use-custom-icon', 'false');
    }
  });

  const wrapper = doc.body.firstChild as HTMLElement;
  return wrapper ? wrapper.innerHTML : html;
};

interface EditContentItemModalProps {
  contentItem: ContentItem;
  moduleId: string;
  courseLanguage?: string; // Course language code for AI generation
  courseCompleted?: boolean; // AI content regeneration is only allowed once course generation is fully complete
  onClose: () => void;
  onSuccess: () => void;
}

type EditorMode = 'visual' | 'preview';

/**
 * Modal for editing content items with dual-mode editor
 * Modes: Visual (contenteditable with full style preservation), Preview
 * Features: autosave, optimistic updates, draft caching, validation
 */
export default function EditContentItemModal({ contentItem, moduleId, courseLanguage = 'en-US', courseCompleted = true, onClose, onSuccess }: EditContentItemModalProps) {
  const queryClient = useQueryClient();
  const is_audio_type = contentItem.type === 'html_with_audio';
  const isRTL = courseLanguage.startsWith('ar');
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('visual'); // Default to Visual mode
  const [isRegeneratingAudio, setIsRegeneratingAudio] = useState(false);
  const [isGeneratingNarration, setIsGeneratingNarration] = useState(false);
  // AI content (HTML) regeneration — completes a broken/incomplete lesson
  const [isRegeneratingContent, setIsRegeneratingContent] = useState(false);
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenImages, setRegenImages] = useState(false);
  const [showRemoveAudioConfirm, setShowRemoveAudioConfirm] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<string>(() => detectTheme(contentItem.slide_structure?.[0]?.html_content || contentItem.content || ''));

  // Track audio sync status
  const [audioOutOfSync, setAudioOutOfSync] = useState(false);
  const lastNarrationUsedForAudio = useRef(
    (contentItem as any).narration ||
    (contentItem.slide_structure?.[0] as any)?.narration ||
    contentItem.scenes?.[0]?.narration ||
    ''
  );

  // Initialize form with current content item data
  // Sanitize initial HTML to remove leading icons from learning-objectives
  const initialHtmlContent = stripLeadingIconsFromContent(
    contentItem.slide_structure?.[0]?.html_content || contentItem.content || ''
  );

  // A freshly-created OR emptied content item has no visible body text (the
  // themed <head>/<style> still make the raw HTML non-empty), so detect
  // emptiness on the stripped body text — the AI action is "Generate", not
  // "Regenerate". Once it has real content, it's "Regenerate".
  const isFreshContent = isHtmlEffectivelyEmpty(initialHtmlContent);
  const aiVerb = isFreshContent ? 'Generate' : 'Regenerate';
  // An uploaded (PDF/Word) item is read-only HTML: we show its rendered S3
  // content and only allow editing the title / time — no HTML editor / AI.
  const isUploaded = (contentItem as any).content_source === 'uploaded';
  const uploadedHtmlUrl = contentItem.media_assets?.s3_html_url || '';
  const uploadedFile = (contentItem as any).uploaded_file as
    | { name?: string; url?: string; file_type?: string; uploaded_at?: string }
    | undefined;
  const aiVerbing = isFreshContent ? 'Generating' : 'Regenerating';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset: _reset,
  } = useForm<ContentItemFormData>({
    // Narration length is validated only for narrated types (html_with_audio).
    // Plain `html` items have no narration UI, so a long seeded narration must not block saving.
    resolver: yupResolver(getContentItemValidationSchema({ validateNarration: is_audio_type })) as any,
    defaultValues: {
      title: contentItem.title || '',
      html_content: initialHtmlContent,
      // Try multiple possible locations for narration
      // Priority: direct narration field first, then slide/scene
      narration: (contentItem as any).narration ||
                 (contentItem.slide_structure?.[0] as any)?.narration ||
                 contentItem.scenes?.[0]?.narration ||
                 '',
      estimated_time: {
        hours: contentItem.estimated_time?.hours || 0,
        minutes: contentItem.estimated_time?.minutes || 0,
      },
      media_assets: {
        s3_audio_url: contentItem.media_assets?.s3_audio_url || '',
      },
    },
  });

  // Watch all form fields to detect changes
  const formValues = watch();

  // Sanitize content on mount to remove leading icons from learning-objectives
  useEffect(() => {
    const currentContent = formValues.html_content;
    if (currentContent) {
      const sanitizedContent = stripLeadingIconsFromContent(currentContent);
      if (sanitizedContent !== currentContent) {
        setValue('html_content', sanitizedContent, { shouldDirty: false });
      }
    }
  }, []); // Only run on mount

  // Extract styles and content for preview
  const getPreviewData = useMemo(() => {
    const htmlContent = formValues.html_content || '';

    // Extract CSS from <style> tags
    const styleMatches = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
    const extractedStyles = styleMatches
      .map((styleTag) => styleTag.replace(/<\/?style[^>]*>/gi, ''))
      .join('\n');

    // Extract body content or use entire HTML
    let bodyContent = htmlContent;
    const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      bodyContent = bodyMatch[1];
    }

    // Sanitize body content
    const sanitizedHTML = DOMPurify.sanitize(bodyContent, {
      ALLOWED_TAGS: [
        'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'u', 's', 'b', 'i',
        'ul', 'ol', 'li', 'a', 'img', 'table', 'tr', 'td', 'th', 'thead', 'tbody',
        'pre', 'code', 'blockquote', 'br', 'hr', 'span', 'div', 'section', 'article',
        'header', 'footer', 'main', 'aside', 'nav', 'figure', 'figcaption', 'button'
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'id', 'style', 'target', 'rel',
        'colspan', 'rowspan', 'width', 'height', 'data-*', 'loading'
      ],
      ALLOW_DATA_ATTR: true,
    });

    return { styles: extractedStyles, content: sanitizedHTML };
  }, [formValues.html_content]);

  // Get preview HTML with contenteditable attributes removed (for read-only preview)
  const getPreviewHTML = useMemo(() => {
    const htmlContent = formValues.html_content || '';
    // Remove all contenteditable attributes to make preview read-only
    return htmlContent.replace(/\s*contenteditable\s*=\s*["']?(true|false)["']?/gi, '');
  }, [formValues.html_content]);

  const { styles: _previewStyles, content: _previewContent } = getPreviewData;

  /**
   * Size of the uploaded file. The size is not stored on the content item, and the
   * item's html_content is only a themed wrapper around the file, so its length says
   * nothing useful — the real figure comes from a HEAD request on the stored file URL.
   * Null while loading, and stays null if the object storage does not return
   * Content-Length or blocks the cross-origin HEAD, in which case the size is simply
   * omitted rather than shown wrong.
   */
  const [uploadedFileSize, setUploadedFileSize] = useState<number | null>(null);

  useEffect(() => {
    const fileUrl = uploadedFile?.url;
    if (!isUploaded || !fileUrl) return;
    let cancelled = false;
    fetch(fileUrl, { method: 'HEAD' })
      .then((res) => {
        const length = Number(res.headers.get('content-length'));
        if (!cancelled && Number.isFinite(length) && length > 0) setUploadedFileSize(length);
      })
      .catch(() => {
        /* size omitted */
      });
    return () => {
      cancelled = true;
    };
  }, [isUploaded, uploadedFile?.url]);

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
  };

  // Calculate content statistics
  const contentStats = useMemo(() => {
    // Component counts are matched against the markup with <head>/<style>/<script>
    // removed, so CSS class names (.learning-checkpoint, .skillzen-code-snippet)
    // don't register as phantom components.
    const htmlContent = (formValues.html_content || '')
      .replace(/<head[\s\S]*?<\/head>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '');
    // Words come from the parsed document, not this stripped string: the themed
    // items carry a <title> that the head regex does not always cover, and its text
    // was being counted (an emptied item read as 7 words — the title's length).
    const textContent = extractVisibleText(formValues.html_content);
    const wordCount = textContent ? textContent.split(/\s+/).filter(Boolean).length : 0;
    const charCount = textContent.length;
    // 0 words means 0 minutes, not the 1 that Math.ceil would round up to.
    const readingTime = wordCount === 0 ? 0 : Math.ceil(wordCount / 200); // ~200 wpm
    const fileSize = new Blob([htmlContent]).size;
    const fileSizeKB = (fileSize / 1024).toFixed(1);

    // Count components
    const imageCount = (htmlContent.match(/<img/gi) || []).length;
    const audioCount = (htmlContent.match(/<audio/gi) || []).length;
    const videoCount = (htmlContent.match(/<video/gi) || []).length;
    const embedCount = (htmlContent.match(/<iframe/gi) || []).length;
    const tableCount = (htmlContent.match(/<table/gi) || []).length;
    const codeBlockCount = (htmlContent.match(/skillzen-code-snippet/gi) || []).length;
    const knowledgeCheckCount = (htmlContent.match(/learning-checkpoint/gi) || []).length;

    return {
      wordCount,
      charCount,
      readingTime,
      fileSizeKB,
      imageCount,
      audioCount,
      videoCount,
      embedCount,
      tableCount,
      codeBlockCount,
      knowledgeCheckCount,
    };
  }, [formValues.html_content]);

  // Track dirty state
  useEffect(() => {
    const subscription = watch(() => {
      setIsDirty(true);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Track audio sync status - mark out of sync when narration changes
  useEffect(() => {
    const currentNarration = formValues.narration || '';
    const hasAudio = formValues.media_assets?.s3_audio_url;

    // If audio exists and narration changed from last used for audio generation
    if (hasAudio && currentNarration !== lastNarrationUsedForAudio.current && lastNarrationUsedForAudio.current) {
      setAudioOutOfSync(true);
    } else if (!hasAudio) {
      setAudioOutOfSync(false);
    }
  }, [formValues.narration, formValues.media_assets?.s3_audio_url]);

  // Unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Mutation for updating content item with optimistic updates
  const updateMutation = useMutation({
    mutationFn: (data: ContentItemFormData) => courseService.updateContentItem(contentItem._id!, data as any),
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ['moduleContent', moduleId] });
      const previousData = queryClient.getQueryData(['moduleContent', moduleId]);

      queryClient.setQueryData(['moduleContent', moduleId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          content_items: old.content_items?.map((item: ContentItem) =>
            item._id === contentItem._id ? { ...item, ...newData } : item
          ),
        };
      });

      return { previousData };
    },
    onError: (error: any, _newData, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['moduleContent', moduleId], context.previousData);
      }

      const errorData = error.response?.data;
      if (errorData?.meta_data?.errors && Array.isArray(errorData.meta_data.errors)) {
        errorData.meta_data.errors.forEach((err: string) => {
          toast.error(err, { duration: 5000 });
        });
      } else if (errorData?.error?.error_description) {
        toast.error(errorData.error.error_description);
      } else {
        toast.error(error.message || 'Failed to update content item');
      }
    },
    onSuccess: () => {
      toast.success('Content item updated successfully!');
      setIsDirty(false);
      onSuccess();
      onClose();
    },
  });

  // Handle form submission
  const onSubmit = (data: ContentItemFormData) => {
    // Uploaded (PDF/Word) items are read-only HTML — persist only the editable
    // metadata (title / description / time), never the html_content, so the
    // uploaded document is preserved.
    if (isUploaded) {
      // Persist only the editable metadata — never html_content.
      updateMutation.mutate({
        title: cleanText(data.title),
        narration: cleanText((data as any).narration),
        estimated_time: (data as any).estimated_time,
        media_assets: (data as any).media_assets,
      } as any);
      return;
    }
    // Clean up HTML content before saving - remove contenteditable attributes
    const cleanedData = {
      ...data,
      // Sanitize free-text before save (VAPT May 2026, #12/#13)
      title: cleanText(data.title),
      narration: cleanText((data as any).narration),
      html_content: data.html_content?.replace(/\s*contenteditable\s*=\s*["']?(true|false)["']?/gi, '') || '',
    };
    updateMutation.mutate(cleanedData);
  };

  // Remove the uploaded file (keeps the item; resets it to empty).
  const [isRemovingUpload, setIsRemovingUpload] = useState(false);
  const handleRemoveUpload = async () => {
    setIsRemovingUpload(true);
    const loading = toast.loading('Removing uploaded file...');
    try {
      await courseService.removeContentItemUpload(contentItem._id!);
      toast.success('Uploaded file removed', { id: loading });
      onSuccess(); // refresh + close
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove uploaded file', { id: loading });
    } finally {
      setIsRemovingUpload(false);
    }
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    onClose();
  };

  // Generate narration from HTML content using AI (backend)
  const handleGenerateNarration = async () => {
    // Authored lessons need HTML; uploaded PDFs are parsed on the backend, so
    // no HTML is required for them.
    if (!isUploaded && (!formValues.html_content || formValues.html_content.trim().length === 0)) {
      toast.error('Please add HTML content first before generating narration');
      return;
    }

    setIsGeneratingNarration(true);
    try {
      toast.loading(
        isUploaded
          ? 'AI is reading the PDF and generating narration... (10-30 seconds)'
          : 'AI is analyzing HTML and generating narration... (10-30 seconds)',
        { id: 'narration-gen' }
      );

      // Call backend API to generate narration using AI
      const result = await courseService.generateNarration(
        contentItem._id!,
        formValues.html_content,
        {
          language_code: courseLanguage, // Use course language
        }
      );

      console.log('✅ Narration API result received:', result);
      console.log('Result keys:', Object.keys(result));
      console.log('Result.narration:', result.narration ? 'EXISTS' : 'MISSING');
      console.log('Result.data:', result.data ? 'EXISTS' : 'MISSING');

      // Service already returns the data object, so result should be: { narration, word_count, ... }
      let narrationText = '';
      let wordCount = 0;
      let duration = 0;

      // The service returns response.data.data directly, so result IS the data object
      if (result && result.narration) {
        narrationText = result.narration;
        wordCount = result.word_count || result.narration.split(/\s+/).filter(Boolean).length;
        duration = result.estimated_duration || Math.ceil(wordCount / 2.5);

        console.log('✅ Narration extracted successfully:', {
          textLength: narrationText.length,
          wordCount,
          duration,
          preview: narrationText.substring(0, 100) + '...'
        });
      } else {
        // Log what we actually received to debug
        console.error('❌ Unexpected result structure:', {
          result,
          hasNarration: !!result?.narration,
          hasData: !!result?.data,
          keys: result ? Object.keys(result) : []
        });
        throw new Error('No narration found in API response');
      }

      // Update narration textarea with AI-generated text
      setValue('narration', narrationText, { shouldValidate: true, shouldDirty: true });

      toast.success(
        `✅ AI Narration Generated!\n📊 ${wordCount} words | ⏱️ ~${duration}s audio | 🌍 ${courseLanguage}`,
        { id: 'narration-gen', duration: 5000 }
      );
    } catch (error: any) {
      console.error('Narration generation failed:', error);
      toast.error(error.message || 'Failed to generate narration', { id: 'narration-gen' });
    } finally {
      setIsGeneratingNarration(false);
    }
  };

  // Regenerate the HTML content with AI (completes a broken/incomplete lesson).
  // Feeds the current title + optional feedback + the current (partial) HTML to
  // the backend content-item agent; on success replaces the editor content.
  const handleRegenerateContent = async () => {
    const id = contentItem._id!;
    setIsRegeneratingContent(true);
    try {
      toast.loading('AI is regenerating the content...', { id: 'content-regen' });

      // Fire-and-forget: kicks off generation in the background and returns
      // immediately, then poll until the backend finishes saving. This avoids
      // the request timing out on long generations.
      await courseService.regenerateContentItemHtml(id, {
        title: formValues.title || undefined,
        feedback: regenFeedback.trim() || undefined,
        regenerate_images: regenImages,
      });

      await new Promise<void>((resolve, reject) => {
        const startedAt = Date.now();
        const tick = async () => {
          try {
            const status = await courseService.getContentItemHtmlStatus(id);
            if (status.status === 'completed') return resolve();
            if (status.status === 'failed') return reject(new Error(status.error || 'Regeneration failed'));
          } catch {
            // transient poll error — keep trying
          }
          if (Date.now() - startedAt > 5 * 60 * 1000) {
            return reject(new Error('Regeneration timed out. Please try again.'));
          }
          setTimeout(tick, 3000);
        };
        setTimeout(tick, 2500);
      });

      // The backend regenerated, uploaded to S3 and SAVED the content item.
      // Close + refresh so the freshly persisted content renders from the DB.
      toast.success('✅ Content regenerated & saved.', { id: 'content-regen', duration: 4000 });
      setShowRegenDialog(false);
      setRegenFeedback('');
      setRegenImages(false);
      onSuccess();
    } catch (error: any) {
      console.error('Content regeneration failed:', error);
      toast.error(error.message || 'Failed to regenerate content', { id: 'content-regen' });
    } finally {
      setIsRegeneratingContent(false);
    }
  };

  // Remove audio file
  const handleRemoveAudio = () => {
    setShowRemoveAudioConfirm(true);
  };

  const confirmRemoveAudio = () => {
    setValue('media_assets.s3_audio_url', '', { shouldValidate: true, shouldDirty: true });
    toast.success('Audio removed');
    setShowRemoveAudioConfirm(false);
  };

  // Handle audio regeneration (simplified - no save required)
  const handleRegenerateAudio = async () => {
    if (!formValues.narration || formValues.narration.trim().length === 0) {
      toast.error('Please add narration text before generating audio');
      return;
    }

    // Generate audio directly without requiring save
    await generateAudio();
  };


  // Generate audio from narration using TTS
  const generateAudio = async () => {
    setIsRegeneratingAudio(true);
    try {
      // Save narration first if dirty to ensure audio matches latest narration
      if (isDirty && formValues.narration) {
        toast.loading('Saving narration...', { id: 'save-narration', duration: 1000 });
        await courseService.updateContentItem(contentItem._id!, {
          title: formValues.title,
          html_content: formValues.html_content,
          narration: formValues.narration,
          estimated_time: formValues.estimated_time,
          media_assets: formValues.media_assets,
        } as any);
        queryClient.invalidateQueries({ queryKey: ['moduleContent', moduleId] });
        toast.success('Narration saved!', { id: 'save-narration', duration: 1000 });
      }

      toast.loading(`Generating audio in ${courseLanguage}... This may take 10-30 seconds`, { duration: 2000 });

      // No speed override — the backend applies the voice + speaking rate
      // configured on the course, so every lesson narrates consistently.
      const result = await courseService.regenerateContentItemAudio(
        contentItem._id!,
        {
          language_code: courseLanguage, // Use course language for TTS
        }
      );

      console.log('Audio generation result:', result);
      console.log('Result keys:', Object.keys(result));
      console.log('s3_audio_url:', result.s3_audio_url);

      // Extract audio URL (handle nested structure)
      const audioUrl = result.s3_audio_url || result.data?.s3_audio_url;

      if (!audioUrl) {
        console.error('No audio URL in result:', result);
        throw new Error('No audio URL returned from server');
      }

      console.log('✅ Setting audio URL:', audioUrl);

      // Update form with new audio URL
      setValue('media_assets.s3_audio_url', audioUrl, {
        shouldValidate: true,
        shouldDirty: true
      });

      const duration = result.duration_seconds || Math.ceil((result.narration?.split(' ').length || 0) / 2.5);
      toast.success(`✅ Audio Generated! | ⏱️ ~${duration}s | 🌍 ${courseLanguage}`, {
        duration: 5000
      });

      // Reset audio sync status after successful generation
      // Update the reference narration to the current narration
      lastNarrationUsedForAudio.current = formValues.narration || '';
      setAudioOutOfSync(false);
    } catch (error: any) {
      console.error('Audio generation error:', error);
      toast.error(error.message || 'Failed to regenerate audio');
    } finally {
      setIsRegeneratingAudio(false);
    }
  };

  return (
    <>
      {/* Main Modal - Responsive */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-1 sm:p-2 md:p-[2vh]" role="dialog" aria-modal="true" aria-labelledby="modal-title" dir="ltr">
        {/* Protect modal UI from dark-mode content styles leaking */}
        <style dangerouslySetInnerHTML={{ __html: `
          .content-edit-modal .modal-sidebar, .content-edit-modal .modal-sidebar * { color: #111827 !important; direction: ltr !important; text-align: left !important; }
          .content-edit-modal .modal-sidebar input[dir="rtl"], .content-edit-modal .modal-sidebar textarea[dir="rtl"] { direction: rtl !important; text-align: right !important; }
          .content-edit-modal .modal-sidebar .text-center, .content-edit-modal .modal-sidebar .text-center * { text-align: center !important; }
          .content-edit-modal .modal-sidebar input, .content-edit-modal .modal-sidebar textarea, .content-edit-modal .modal-sidebar select { color: #111827 !important; background-color: #fff !important; }
          .content-edit-modal .modal-sidebar label { color: #111827 !important; }
          .content-edit-modal .modal-header, .content-edit-modal .modal-header * { color: #111827 !important; }
          .content-edit-modal .modal-header .text-white { color: #fff !important; }
          .content-edit-modal .modal-header .text-gray-500 { color: #6b7280 !important; }
          .content-edit-modal .modal-header .text-gray-400 { color: #9ca3af !important; }
          .content-edit-modal .modal-footer, .content-edit-modal .modal-footer * { color: #111827 !important; }
          .content-edit-modal .modal-footer .text-white { color: #fff !important; }
          .content-edit-modal .modal-footer .text-orange-600 { color: #ea580c !important; }
        ` }} />
        <div className="content-edit-modal bg-white w-full h-full rounded-lg sm:rounded-xl shadow-2xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="modal-header flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 id="modal-title" className="text-sm font-bold text-gray-900">Edit Content</h2>
              <span className="px-2 py-0.5 bg-gray-100 text-[11px] font-medium text-gray-500 rounded-md">{contentItem.type?.replace(/_/g, ' ')}</span>
            </div>
            <div className="flex items-center gap-2">
              {isUploaded ? (
                <span className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Uploaded file — content is read-only
                </span>
              ) : (
                <>
                  {/* Regenerate with AI — completes a broken/incomplete lesson.
                      Only enabled once course generation is fully complete. */}
                  <button
                    type="button"
                    onClick={() => setShowRegenDialog(true)}
                    disabled={isRegeneratingContent || editorMode === 'preview' || !courseCompleted}
                    title={!courseCompleted ? 'Available once course generation is fully complete' : `${aiVerb} this content with AI`}
                    className="px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isRegeneratingContent ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {aiVerb} with AI
                  </button>
                  <div className="w-px h-6 bg-gray-200"></div>
                  {/* Mode Toggle */}
                  <div className="flex bg-gray-100 rounded-lg p-0.5">
                    <button
                      type="button"
                      onClick={() => setEditorMode('visual')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                        editorMode === 'visual'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <EditIcon className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorMode('preview')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                        editorMode === 'preview'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview
                    </button>
                  </div>
                </>
              )}
              <div className="w-px h-6 bg-gray-200"></div>
              <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors" type="button">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Form Content - Responsive Layout */}
          <form onSubmit={handleSubmit(onSubmit as any)} className="flex-1 overflow-hidden flex flex-col md:flex-row">
            {/* Editor Section - Full width on mobile, 70% on desktop */}
            <div className="flex-1 flex flex-col md:border-r border-gray-200 order-2 md:order-1">

              {/* Validation Error Banner - Sticky */}
              {errors.html_content && (
                <div className="sticky top-0 z-30 bg-red-50 border-b-2 border-red-300 px-6 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center mt-0.5">
                      <AlertCircle className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-900">Content Validation Error</p>
                      <p className="text-xs text-red-700 mt-1">{errors.html_content.message}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Editor Area - Full Height */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 bg-gray-50 scrollbar-hide">
                {/* Uploaded (PDF/Word) — read-only render of the stored HTML from
                    S3 in a plain (NON-sandboxed) iframe so Chrome's PDF viewer
                    works. No HTML editing; only the metadata panel is editable. */}
                {isUploaded ? (
                  <div className="bg-white rounded-lg h-full min-h-[500px] flex flex-col">
                    {uploadedHtmlUrl ? (
                      <iframe
                        src={uploadedHtmlUrl}
                        title={contentItem.title}
                        className="w-full flex-1 min-h-[500px] border-0 rounded-lg bg-white"
                      />
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
                        Uploaded content is not available.
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-indigo-500" />
                      Uploaded file — content can't be edited here.
                    </div>
                  </div>
                ) : editorMode === 'visual' ? (
                  <div className="max-w-full overflow-x-hidden content-editor-wrap">
                    <style dangerouslySetInnerHTML={{ __html: `.content-editor-wrap .editor-content-preview::-webkit-scrollbar { display: none; } .content-editor-wrap .editor-content-preview { scrollbar-width: none; -ms-overflow-style: none; } .content-editor-wrap > div::-webkit-scrollbar { display: none; } .content-editor-wrap > div { scrollbar-width: none; -ms-overflow-style: none; }` }} />
                    <VisualContentEditor
                      initialContent={formValues.html_content}
                      onChange={(html) => setValue('html_content', html, { shouldValidate: true, shouldDirty: true })}
                      onSave={() => handleSubmit(onSubmit as any)()}
                      isRTL={isRTL}
                    />
                  </div>
                ) : null}

                {!isUploaded && editorMode === 'preview' && (
                  <div className="bg-white rounded-lg p-4 max-w-full h-full flex flex-col">
                    {/* Audio Player at top - only show if audio exists */}
                    {formValues.media_assets?.s3_audio_url && (
                      <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-lg">🔊</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-purple-900 mb-1">Audio Narration</p>
                            <audio
                              controls
                              controlsList="nodownload"
                              className="w-full h-8"
                              src={formValues.media_assets.s3_audio_url}
                            >
                              Your browser does not support audio playback.
                            </audio>
                          </div>
                        </div>
                      </div>
                    )}

                    <iframe
                      srcDoc={getPreviewHTML}
                      sandbox="allow-scripts allow-same-origin"
                      className="w-full flex-1 min-h-[500px] border-0 rounded-lg bg-white"
                      title="Interactive Content Preview"
                    />
                    <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      Interactive Preview - Click buttons to test functionality
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - Responsive: Full width mobile, 400px desktop */}
            <div
              dir="ltr"
              className="modal-sidebar w-full md:w-[400px] flex flex-col bg-white border-b md:border-b-0 md:border-l border-gray-200 order-1 md:order-2"
            >
              <div className={`flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 max-h-[40vh] md:max-h-none scrollbar-hide ${editorMode === 'preview' ? 'cursor-not-allowed [&_input]:cursor-not-allowed [&_textarea]:cursor-not-allowed [&_button]:pointer-events-none [&_select]:pointer-events-none' : ''}`}>
                {/* Title */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
                    <Type className="w-3.5 h-3.5 text-gray-400" />
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('title')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    readOnly={editorMode === 'preview'}
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                      errors.title ? 'border-red-400 bg-red-50/50' : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'
                    } ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}
                    placeholder="Content title..."
                  />
                  {errors.title && <p className="text-xs text-red-600 mt-1.5">{errors.title.message}</p>}
                </div>

                {/* Theme Selector - hidden from UI */}

                {/* Narration - only for html_with_audio */}
                {is_audio_type && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Narration Text</label>
                    {/* Derives narration from the lesson source: an uploaded PDF is
                        parsed on the backend ("Generate from PDF"); an authored
                        lesson uses its HTML ("Generate from HTML"). */}
                    <button
                      type="button"
                      onClick={handleGenerateNarration}
                      disabled={isGeneratingNarration || (!isUploaded && !formValues.html_content)}
                      className="px-2.5 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 text-[11px] font-medium flex items-center gap-1"
                    >
                      {isGeneratingNarration ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Analyzing...
                        </>
                      ) : (
                        isUploaded ? 'Generate from PDF' : 'Generate from HTML'
                      )}
                    </button>
                  </div>
                  <textarea
                    {...register('narration')}
                    rows={6}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    readOnly={editorMode === 'preview'}
                    className={`w-full px-3.5 py-2.5 border rounded-lg text-sm bg-gray-50/50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none transition-all scrollbar-hide border-gray-200 ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}
                    placeholder="Enter narration or click 'Generate from HTML' for AI-powered narration..."
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      {formValues.narration?.length || 0} / 5000 characters
                    </p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-gray-600">
                        📝 {formValues.narration?.split(' ').filter(w => w).length || 0} words
                      </span>
                      <span className="text-gray-600">
                        ⏱️ ~{Math.ceil((formValues.narration?.split(' ').filter(w => w).length || 0) / 2.5)}s audio
                      </span>
                    </div>
                  </div>
                  {formValues.narration && formValues.narration.length > 0 && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-700 flex items-center gap-1">
                        ✅ Narration ready for TTS audio generation
                      </p>
                    </div>
                  )}
                </div>
                )}

                {/* Audio - only for html_with_audio */}
                {is_audio_type && (
                <div>
                  <label className="text-sm font-semibold text-gray-800 mb-3 block">Audio Management</label>

                  {/* Current Audio Preview - Only show if audio exists */}
                  {formValues.media_assets?.s3_audio_url && (
                    <div className={`mb-4 p-4 rounded-xl ${
                      audioOutOfSync
                        ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-400'
                        : 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {audioOutOfSync ? (
                            <>
                              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                              <span className="text-sm font-bold text-orange-900">⚠️ Audio Out of Sync</span>
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                              <span className="text-sm font-bold text-green-900">✓ Audio Ready</span>
                            </>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveAudio}
                          className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors font-semibold"
                        >
                          Remove
                        </button>
                      </div>

                      {audioOutOfSync && (
                        <div className="mb-2 p-2 bg-orange-100 border border-orange-300 rounded-lg">
                          <p className="text-xs text-orange-900 font-semibold">
                            ⚠️ Narration changed! Click "Generate Audio" to update.
                          </p>
                        </div>
                      )}

                      <audio
                        controls
                        controlsList="nodownload"
                        className="w-full mt-2"
                        src={formValues.media_assets.s3_audio_url}
                      >
                        Your browser does not support audio playback.
                      </audio>
                      <p className={`text-xs mt-2 flex items-center gap-1 ${audioOutOfSync ? 'text-orange-700' : 'text-green-700'}`}>
                        🔊 Audio file attached
                      </p>
                    </div>
                  )}

                  {/* Audio Options - Two Methods */}
                  <div className="space-y-3">
                    {/* Option 1: Generate from Narration (AI TTS) */}
                    <div className="border-2 border-purple-200 rounded-xl p-4 bg-purple-50">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                          <span className="text-white text-lg">🎙️</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-purple-900">Generate from Narration</h4>
                          <p className="text-xs text-purple-700">AI-powered Text-to-Speech</p>
                        </div>
                      </div>

                      {/* Status Indicator */}
                      {formValues.narration ? (
                        <div className="mb-3 p-2 bg-green-100 border border-green-300 rounded-lg">
                          <p className="text-xs text-green-800 flex items-center gap-1">
                            ✓ Narration ready ({formValues.narration.split(' ').filter(w => w).length} words,
                            ~{Math.ceil((formValues.narration.split(' ').filter(w => w).length || 0) / 2.5)}s audio)
                          </p>
                        </div>
                      ) : (
                        <div className="mb-3 p-2 bg-orange-100 border border-orange-300 rounded-lg">
                          <p className="text-xs text-orange-800 flex items-center gap-1">
                            ⚠️ Add narration text first
                          </p>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleRegenerateAudio}
                        disabled={isRegeneratingAudio || !formValues.narration}
                        className={`w-full px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold ${
                          audioOutOfSync
                            ? 'bg-orange-600 hover:bg-orange-700 text-white animate-pulse'
                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                        }`}
                      >
                        {isRegeneratingAudio ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Generating...
                          </>
                        ) : audioOutOfSync ? (
                          <>
                            🔄 Update Audio
                          </>
                        ) : (
                          <>
                            🎙️ Generate Audio
                          </>
                        )}
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-white text-gray-500 font-semibold">OR</span>
                      </div>
                    </div>

                    {/* Option 2: Upload Audio File */}
                    <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                            <span className="text-white text-lg">📁</span>
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-blue-900">
                              {formValues.media_assets?.s3_audio_url ? 'Uploaded Audio' : 'Upload Audio File'}
                            </h4>
                            <p className="text-xs text-blue-700">MP3 or WAV (max 10MB)</p>
                          </div>
                        </div>
                        {/* Delete button - only show when audio exists */}
                        {formValues.media_assets?.s3_audio_url && (
                          <button
                            type="button"
                            onClick={handleRemoveAudio}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Remove audio"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Show current audio if uploaded, with option to replace */}
                      {formValues.media_assets?.s3_audio_url ? (
                        <div className="space-y-3">
                          <div className="p-3 bg-white rounded-lg border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-green-600">✓</span>
                              <span className="text-sm text-gray-700">
                                Audio file uploaded
                              </span>
                            </div>
                            <audio
                              controls
                              controlsList="nodownload"
                              className="w-full h-8"
                              src={formValues.media_assets.s3_audio_url}
                            >
                              Your browser does not support audio playback.
                            </audio>
                          </div>
                          <MediaUploader
                            label=""
                            description="Drop a new file to replace"
                            currentUrl=""
                            onUploadComplete={(url) => {
                              setValue('media_assets.s3_audio_url', url, { shouldValidate: true, shouldDirty: true });
                              lastNarrationUsedForAudio.current = formValues.narration || '';
                              setAudioOutOfSync(false);
                              toast.success('Audio replaced successfully');
                            }}
                            accept="audio/*"
                            mediaType="audio"
                            maxSize={10}
                          />
                        </div>
                      ) : (
                        <MediaUploader
                          label=""
                          description="Drag & drop or click to browse"
                          currentUrl=""
                          onUploadComplete={(url) => {
                            setValue('media_assets.s3_audio_url', url, { shouldValidate: true, shouldDirty: true });
                            lastNarrationUsedForAudio.current = formValues.narration || '';
                            setAudioOutOfSync(false);
                          }}
                          accept="audio/*"
                          mediaType="audio"
                          maxSize={10}
                        />
                      )}
                    </div>
                  </div>
                </div>
                )}

                {/* Duration */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
                    <Clock className="w-3.5 h-3.5 text-gray-400" />
                    Estimated Time
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <input
                        type="number"
                        {...register('estimated_time.hours')}
                        min="0"
                        max="100"
                        readOnly={editorMode === 'preview'}
                        placeholder="0"
                        className={`w-full px-3.5 py-2.5 pr-14 border rounded-lg text-sm bg-gray-50/50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">hours</span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        {...register('estimated_time.minutes')}
                        min="0"
                        max="59"
                        readOnly={editorMode === 'preview'}
                        placeholder="0"
                        className={`w-full px-3.5 py-2.5 pr-14 border rounded-lg text-sm bg-gray-50/50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">mins</span>
                    </div>
                  </div>
                </div>

                {/* Content Statistics. An uploaded item has no authored HTML to measure —
                    word count, read time and component counts would all describe the
                    themed wrapper around the file, not the file — so it reports the file
                    instead. */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
                    <BarChart3 className="w-3.5 h-3.5 text-gray-400" />
                    Content Statistics
                  </label>
                  {isUploaded ? (
                    <div className="p-3 bg-gray-50/80 border border-gray-200 rounded-lg">
                      <div className="flex items-start gap-2.5">
                        <FileText className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">File uploaded</p>
                          {uploadedFile?.name && (
                            <p className="text-xs text-gray-600 mt-0.5 break-words">{uploadedFile.name}</p>
                          )}
                          <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
                            {uploadedFileSize !== null && (
                              <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                                {formatFileSize(uploadedFileSize)}
                              </span>
                            )}
                            {uploadedFile?.uploaded_at && (
                              <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                                {new Date(uploadedFile.uploaded_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                  <div className="p-3 bg-gray-50/80 border border-gray-200 rounded-lg space-y-2.5">
                    {/* Main Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm">
                        <div className="text-base font-bold text-blue-600">{contentStats.wordCount}</div>
                        <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Words</div>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm">
                        <div className="text-base font-bold text-emerald-600">{contentStats.readingTime}m</div>
                        <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Read Time</div>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm">
                        <div className="text-base font-bold text-violet-600">{contentStats.fileSizeKB}KB</div>
                        <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Size</div>
                      </div>
                    </div>

                    {/* Component Counts */}
                    {(contentStats.imageCount > 0 || contentStats.audioCount > 0 || contentStats.videoCount > 0 || contentStats.embedCount > 0 || contentStats.tableCount > 0 || contentStats.codeBlockCount > 0 || contentStats.knowledgeCheckCount > 0) && (
                      <div className="pt-2 border-t border-gray-200">
                        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Components</div>
                        <div className="flex flex-wrap gap-1.5 text-xs">
                          {contentStats.imageCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.imageCount} {contentStats.imageCount === 1 ? 'Image' : 'Images'}
                            </span>
                          )}
                          {contentStats.audioCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.audioCount} {contentStats.audioCount === 1 ? 'Audio' : 'Audios'}
                            </span>
                          )}
                          {contentStats.videoCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.videoCount} {contentStats.videoCount === 1 ? 'Video' : 'Videos'}
                            </span>
                          )}
                          {contentStats.embedCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.embedCount} {contentStats.embedCount === 1 ? 'Embed' : 'Embeds'}
                            </span>
                          )}
                          {contentStats.tableCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.tableCount} {contentStats.tableCount === 1 ? 'Table' : 'Tables'}
                            </span>
                          )}
                          {contentStats.codeBlockCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.codeBlockCount} Code {contentStats.codeBlockCount === 1 ? 'Block' : 'Blocks'}
                            </span>
                          )}
                          {contentStats.knowledgeCheckCount > 0 && (
                            <span className="px-2 py-1 bg-white rounded-md text-gray-600 border border-gray-100">
                              {contentStats.knowledgeCheckCount} {contentStats.knowledgeCheckCount === 1 ? 'Check' : 'Checks'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className={`modal-footer flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 bg-white ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}>
            <div className="flex items-center gap-2 text-xs">
              {isUploaded && (
                <button
                  type="button"
                  onClick={handleRemoveUpload}
                  disabled={isRemovingUpload}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm border border-red-200 text-red-600 bg-white rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  title="Remove the uploaded file (the content item stays)"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {isRemovingUpload ? 'Removing…' : 'Remove uploaded file'}
                </button>
              )}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="font-medium text-orange-600">Unsaved changes</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting || editorMode === 'preview'}
                className={`px-4 py-2 text-sm border border-gray-200 text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit(onSubmit as any)}
                disabled={isSubmitting || updateMutation.isPending || editorMode === 'preview'}
                className={`px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-semibold shadow-sm ${editorMode === 'preview' ? 'cursor-not-allowed' : ''}`}
              >
                {isSubmitting || updateMutation.isPending ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      <ConfirmationModal
        isOpen={showUnsavedWarning}
        type="warning"
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        onConfirm={confirmClose}
        onCancel={() => setShowUnsavedWarning(false)}
      />

      {/* Remove Audio Confirmation */}
      <ConfirmationModal
        isOpen={showRemoveAudioConfirm}
        type="danger"
        title="Remove Audio"
        message="Are you sure you want to remove the audio file? This action cannot be undone."
        confirmText="Remove Audio"
        cancelText="Cancel"
        onConfirm={confirmRemoveAudio}
        onCancel={() => setShowRemoveAudioConfirm(false)}
      />

      {/* Regenerate with AI — feedback dialog / animated progress */}
      {showRegenDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[fadeIn_0.15s_ease-out]">
            <style dangerouslySetInnerHTML={{ __html: `
              @keyframes fadeIn { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: none; } }
              @keyframes regenBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
            `}} />

            {isRegeneratingContent ? (
              /* ---- Regenerating: polished progress view ---- */
              <div className="px-8 py-10 flex flex-col items-center text-center">
                <div className="relative w-16 h-16 mb-5">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 animate-ping opacity-30" />
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <Sparkles className="w-7 h-7 text-white animate-pulse" />
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-900">{aiVerbing} your content…</h3>
                <p className="text-sm text-gray-500 mt-1.5 max-w-xs">
                  The AI is {isFreshContent ? 'building' : 'rebuilding'} this lesson using your title, module context{regenFeedback.trim() ? ' and feedback' : ''}{regenImages ? ', and generating images' : ''}. This usually takes {regenImages ? '40–90' : '20–60'} seconds.
                </p>
                <div className="w-full max-w-[240px] h-1.5 bg-gray-100 rounded-full overflow-hidden mt-6">
                  <div className="h-full w-1/4 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full" style={{ animation: 'regenBar 1.1s ease-in-out infinite' }} />
                </div>
                <p className="text-[11px] text-gray-400 mt-4">Please keep this window open.</p>
              </div>
            ) : (
              /* ---- Idle: feedback form ---- */
              <>
                <div className="flex items-start gap-3 px-6 pt-6 pb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900">{aiVerb} with AI</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {isFreshContent
                        ? "Generates this lesson's HTML from the title and module context. Add feedback below to steer it."
                        : "Rebuilds this lesson's HTML. Your current content is used as context, so an incomplete lesson gets completed — not replaced blindly."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowRegenDialog(false); setRegenFeedback(''); setRegenImages(false); }}
                    className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <div className="px-6 pb-2">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Feedback / instructions <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={regenFeedback}
                    onChange={(e) => setRegenFeedback(e.target.value)}
                    rows={4}
                    maxLength={4000}
                    autoFocus
                    // Force readable colors so a dark-theme course's leaked content
                    // styles can't render the typed text white-on-white.
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none !text-gray-900 !bg-white placeholder:!text-gray-400"
                    placeholder="e.g. The lesson was cut off after the second heading — complete it and add a summary section."
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">Leave empty to just complete/rebuild from the title & module context.</p>
                    <span className="text-[11px] text-gray-400 tabular-nums">{regenFeedback.length}/4000</span>
                  </div>

                  {/* Regenerate images toggle */}
                  <label className="flex items-start gap-3 mt-3 p-3 rounded-xl border border-gray-200 hover:border-purple-300 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={regenImages}
                      onChange={(e) => setRegenImages(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-gray-800">{aiVerb} images</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Also generate AI images for the lesson. Off = text-only (faster). On adds ~20–40s per image.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="flex items-center justify-end gap-2 px-6 py-4 mt-2 bg-gray-50 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => { setShowRegenDialog(false); setRegenFeedback(''); setRegenImages(false); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRegenerateContent}
                    className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 rounded-lg flex items-center gap-2 shadow-sm transition-all"
                  >
                    <Sparkles className="w-4 h-4" /> {aiVerb}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
